import LoaderImg from "@assets/images/loader.gif";
import HsCodeAutosuggest from "@components/compareTariff/customHscodeAutosuggest";
import CustomSelect from "@components/compareTariff/customSelect";
import { compareTariffApis } from "@components/compareTariff/enum";
import TariffsData from "@components/compareTariff/TariffsData";
import Layout from "@components/layout/layout";
import { captureException } from "@sentry/react";
import axios from "axios";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronDown, ChevronUp } from "react-feather";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { BRAND } from "@core/brand/brand-config";

const IS_SOURCE_ATLAS = BRAND.key === "source_atlas";

const ERROR_MESSAGE = "Something went wrong. Please try again later !!!";

const TooltipWrapper = styled.div`
  .__react_component_tooltip.trademo-tooltip.show.place-bottom::after {
    display: none;
  }
`;

const tradeDirectionEnum = {
  import: "Import",
  export: "Export",
};

const createCountryList = (data) => {
  return Object.entries(data).map(([code, name]) => ({
    value: code,
    label: name,
  }));
};
const maxCountrySelectLimit = 3;
const TariffAnalyser = () => {
  const query = new URLSearchParams(window.location.search);
  const isClassifierRedirect = query.get("redirect") === "hs-classifier";
  const initialTradeDirection = isClassifierRedirect ? (query.get("type") === "export" ? "export" : "import") : "import";
  const initialCountry = isClassifierRedirect ? query.get("country") || null : null;
  const initialHsCode = isClassifierRedirect ? query.get("hscode") || null : null;

  const [selectedCountry, setSelectedCountry] = useState({
    countryOfImport: null,
    countryOfExport: null,
  });
  const [countryOptions, setCountryOptions] = useState({
    countryOfImport: [],
    countryOfExport: [],
  });
  const [selectedHSCode, setSelectedHSCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tradeDirection, setTradeDirection] = useState(initialTradeDirection);
  const [date, setDate] = useState(new Date());
  const [hsCodeInfoData, setHsCodeInfoData] = useState(null);
  const [openAccordions, setOpenAccordions] = useState({ 0: true });
  const [isRedirect, setIsRedirect] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [remainingValueAddCredits, setRemainingValueAddCredits] = useState(0);
  const [countryMapping, setCountryMapping] = useState({});
  const history = useHistory();

  const getCountries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(compareTariffApis.getcountries, { params: { tradeDirection } });
      const coi = createCountryList(data.countryOfImport);
      const coe = createCountryList(data.countryOfExport);
      setCountryMapping({ ...(data.countryOfImport || {}), ...(data.countryOfExport || {}) });

      if (initialCountry) {
        if (tradeDirection === "import") {
          const selectedCoi = coi.find((c) => c.value === initialCountry);
          if (selectedCoi) {
            setSelectedCountry((prev) => ({ ...prev, countryOfImport: selectedCoi }));
          }
        } else {
          const selectedCoe = coe.find((c) => c.value === initialCountry);
          if (selectedCoe) {
            setSelectedCountry((prev) => ({ ...prev, countryOfExport: selectedCoe }));
          }
        }
        await suggestTaxableHscodes(initialHsCode, initialCountry);
        history.replace("compare-tariff");
      }

      setCountryOptions({
        countryOfImport: coi,
        countryOfExport: coe,
      });
      setRemainingValueAddCredits(data?.remaining_value_add_credit);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? ERROR_MESSAGE);
      captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const getduties = async () => {
    try {
      setLoading(true);
      setOpenAccordions({ 0: true });
      const params = {
        hscode: selectedHSCode?.hscode,
        trade_direction: tradeDirection,
        effectiveDate: moment(date).format("YYYY-MM-DD"),
      };

      if (tradeDirection === "import") {
        params.countryOfImport = selectedCountry.countryOfImport?.value;
        params.destination = selectedCountry.countryOfExport?.map((c) => c.value) ?? [];
      } else {
        params.countryOfExport = selectedCountry.countryOfExport?.value;
        params.destination = selectedCountry.countryOfImport?.map((c) => c.value) ?? [];
      }

      const { data } = await axios.post(compareTariffApis.getduties, params);

      setHsCodeInfoData(data?.results || null);
      setRemainingValueAddCredits(data?.remaining_value_add_credit);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? ERROR_MESSAGE);
      captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (key, value) => {
    setSelectedCountry((prev) => ({ ...prev, [key]: value }));
    if ((tradeDirection === "import" && key === "countryOfImport") || (tradeDirection === "export" && key === "countryOfExport")) {
      setSelectedHSCode(null);
    }
  };

  useEffect(() => {
    getCountries();
    if (isClassifierRedirect) {
      setIsRedirect(true);
    } else {
      setIsRedirect(false);
    }
  }, []);

  useEffect(() => {
    setSelectedCountry({ countryOfImport: null, countryOfExport: null });
    setSelectedHSCode(null);
    setHsCodeInfoData(null);
    setDate(new Date());
    getCountries();
  }, [tradeDirection]);

  const suggestTaxableHscodes = async (searchedText = "", initialCountry = "") => {
    try {
      const payload = {
        ...(tradeDirection === "import" ? { countryOfImport: initialCountry } : { countryOfExport: initialCountry }),
        search: searchedText,
        tradeDirection,
        effectiveDate: moment(date).format("YYYY-MM-DD"),
      };

      const { data } = await axios.post(compareTariffApis.landedcostAutosuggest, payload);

      if (data?.suggestions?.length > 0) {
        setSelectedHSCode(data.suggestions[0]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to load suggestions");
    }
  };

  const toggleAccordion = (index) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleClassify = () => {
    const url = `/hs-classifier`;
    history.push(url);
  };

  const downloadDutyReport = async () => {
    try {
      setDownloading(true);
      const payload = {
        input: {
          hscode: selectedHSCode?.hscode,
          trade_direction: tradeDirection,
          effectiveDate: moment(date).format("YYYY-MM-DD"),
          ...(tradeDirection === "import"
            ? {
                countryOfImport: selectedCountry.countryOfImport?.value,
                destination: selectedCountry.countryOfExport?.map((c) => c.value) ?? [],
              }
            : {
                countryOfExport: selectedCountry.countryOfExport?.value,
                destination: selectedCountry.countryOfImport?.map((c) => c.value) ?? [],
              }),
        },
        output: hsCodeInfoData,
      };

      const response = await axios.post(compareTariffApis.generateDutyReport, payload, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      let filename = "Compare_Tariff.xlsx";

      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, "").trim();
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? ERROR_MESSAGE);
      captureException(err);
    } finally {
      setDownloading(false);
    }
  };

  const countryJumper = (countryCode, index) => {
    setOpenAccordions({
      [index]: true,
    });

    setTimeout(() => {
      const el = document.getElementById(`${countryCode}-jumper`);
      if (el) {
        window.scroll({
          top: el.offsetTop - 120,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const isSearchDisabled = useMemo(() => {
    let disabled = false;
    if (tradeDirection === "import") {
      disabled = !selectedCountry?.countryOfImport?.value || !selectedCountry?.countryOfExport || selectedCountry?.countryOfExport?.length === 0 || !selectedHSCode;
    } else {
      disabled = !selectedCountry?.countryOfExport?.value || !selectedCountry?.countryOfImport || selectedCountry?.countryOfImport?.length === 0 || !selectedHSCode;
    }
    return disabled;
  }, [selectedCountry, selectedHSCode, tradeDirection]);

  const filteredImportOptions = countryOptions.countryOfImport.filter((c) => c.value !== selectedCountry.countryOfExport?.value);
  const filteredExportOptions = countryOptions.countryOfExport.filter((c) => c.value !== selectedCountry.countryOfImport?.value);
  const formattedResult = hsCodeInfoData ? Object.entries(hsCodeInfoData) : [];
  const autosuggestCounty = tradeDirection === "import" ? selectedCountry.countryOfImport?.value : selectedCountry.countryOfExport?.value;

  return (
    <Layout type={"compare-tariff"} pageTitle="Tariffs | Trademo Intel" setNotification={() => null}>
      {loading && (
        <div className="loader-container">
          <div className="loader-block">
            <img src={LoaderImg} alt="description of gif" />
          </div>
        </div>
      )}

      <div className="tw-flex tw-items-center tw-mt-2">
        {isRedirect && (
          <>
            <div className="tw-py-2 tw-text-sm tw-text-primary-purple-500 tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-mt-1 tw-shrink-0" onClick={() => history.push("/hs-classifier?redirect=compare-tariff")}>
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#7143f1">
                <path d="M360-216 96-480l264-264 51 51-177 177h630v72H234l177 177-51 51Z" />
              </svg>
              Go Back
            </div>
          </>
        )}
        <div className="tw-w-full tw-flex tw-justify-end">
          {!IS_SOURCE_ATLAS && (
            <div className="tw-bg-remaining-credits tw-p-1 tw-items-center tw-gap-1 tw-flex tw-justify-end tw-w-fit">
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#ff7d1c">
                <path d="M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" />
              </svg>
              <span className="tw-text-gray-900 tw-text-sm tw-font-medium">{`Remaining Value Add Credits: ${remainingValueAddCredits}`}</span>
            </div>
          )}
        </div>
      </div>

      <div className="tw-border tw-border-gray-200 tw-bg-gray-25 tw-rounded-lg tw-shadow-sm tw-p-4 tw-text-gray-900 tw-mt-4">
        <div className="tw-text-sm tw-font-medium tw-mb-1">Trade Direction</div>
        <div className="tw-flex tw-mb-4 tw-border tw-rounded-full tw-w-fit tw-px-1 tw-py-0.5 tw-border-gray-200 tw-bg-gray-50 tw-shadow-button">
          {Object.entries(tradeDirectionEnum).map(([key, label]) => (
            <button onClick={() => setTradeDirection(key)} key={key} className={`tw-px-4 tw-py-0.5 tw-rounded-full tw-text-sm tw-font-medium ${tradeDirection === key ? "tw-bg-primary-purple-500 tw-text-white" : "tw-text-gray-400 tw-bg-white"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-border tw-p-4 tw-rounded-lg tw-border-gray-200 tw-bg-white">
          {tradeDirection === "import" ? (
            <>
              <div className="tw-col-span-3 tw-w-full">
                <label className="tw-block tw-text-sm tw-font-medium tw-mb-1">Select Country of Import*</label>
                <CustomSelect isCountry={true} value={selectedCountry.countryOfImport} options={filteredImportOptions} placeholder="Select Country" onChange={(value) => handleCountryChange("countryOfImport", value)} />
              </div>

              <div className="tw-col-span-7 tw-w-full">
                <label className="tw-text-sm tw-font-medium tw-mb-1 tw-flex tw-items-center tw-gap-1">
                  Select Country of Export*
                  {selectedCountry?.countryOfExport?.length === maxCountrySelectLimit && (
                    <>
                      <svg data-for="max-search" data-tip={`You can compare tariff for only ${maxCountrySelectLimit} countries at a time`} xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#101828">
                        <path d="M454-298h52v-230h-52v230Zm25.79-290.46q11.94 0 20.23-8.08 8.29-8.08 8.29-20.02t-8.08-20.23q-8.08-8.28-20.02-8.28T459.98-637q-8.29 8.08-8.29 20.02t8.08 20.23q8.08 8.29 20.02 8.29Zm.55 472.46q-75.11 0-141.48-28.42-66.37-28.42-116.18-78.21-49.81-49.79-78.25-116.09Q116-405.01 116-480.39q0-75.38 28.42-141.25t78.21-115.68q49.79-49.81 116.09-78.25Q405.01-844 480.39-844q75.38 0 141.25 28.42t115.68 78.21q49.81 49.79 78.25 115.85Q844-555.45 844-480.34q0 75.11-28.42 141.48-28.42 66.37-78.21 116.18-49.79 49.81-115.85 78.25Q555.45-116 480.34-116Zm-.34-52q130 0 221-91t91-221q0-130-91-221t-221-91q-130 0-221 91t-91 221q0 130 91 221t221 91Zm0-312Z" />
                      </svg>
                      <ReactTooltip className="trademo-tooltip" place="top" id="max-search" effect="solid" />
                    </>
                  )}
                </label>

                <CustomSelect maxLimit={maxCountrySelectLimit} isCountry={true} isMulti={true} value={selectedCountry.countryOfExport} options={filteredExportOptions} placeholder="Select Country" onChange={(value) => handleCountryChange("countryOfExport", value)} />
              </div>
            </>
          ) : (
            <>
              <div className="tw-col-span-3 tw-w-full">
                <label className="tw-block tw-text-sm tw-font-medium tw-mb-1">Select Country of Export*</label>
                <CustomSelect isCountry={true} value={selectedCountry.countryOfExport} options={filteredExportOptions} placeholder="Select Country" onChange={(value) => handleCountryChange("countryOfExport", value)} />
              </div>

              <div className="tw-col-span-7 tw-w-full">
                <label className="tw-text-sm tw-font-medium tw-mb-1 tw-flex tw-items-center tw-gap-1">
                  Select Country of Import*
                  {selectedCountry?.countryOfImport?.length === maxCountrySelectLimit && (
                    <>
                      <svg data-for="max-search" data-tip={`You can compare tariff for only ${maxCountrySelectLimit} countries at a time`} xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#101828">
                        <path d="M454-298h52v-230h-52v230Zm25.79-290.46q11.94 0 20.23-8.08 8.29-8.08 8.29-20.02t-8.08-20.23q-8.08-8.28-20.02-8.28T459.98-637q-8.29 8.08-8.29 20.02t8.08 20.23q8.08 8.29 20.02 8.29Zm.55 472.46q-75.11 0-141.48-28.42-66.37-28.42-116.18-78.21-49.81-49.79-78.25-116.09Q116-405.01 116-480.39q0-75.38 28.42-141.25t78.21-115.68q49.79-49.81 116.09-78.25Q405.01-844 480.39-844q75.38 0 141.25 28.42t115.68 78.21q49.81 49.79 78.25 115.85Q844-555.45 844-480.34q0 75.11-28.42 141.48-28.42 66.37-78.21 116.18-49.79 49.81-115.85 78.25Q555.45-116 480.34-116Zm-.34-52q130 0 221-91t91-221q0-130-91-221t-221-91q-130 0-221 91t-91 221q0 130 91 221t221 91Zm0-312Z" />
                      </svg>
                      <ReactTooltip className="trademo-tooltip" place="top" id="max-search" effect="solid" />
                    </>
                  )}
                </label>

                <CustomSelect maxLimit={maxCountrySelectLimit} isCountry={true} isMulti={true} value={selectedCountry.countryOfImport} options={filteredImportOptions} placeholder="Select Country" onChange={(value) => handleCountryChange("countryOfImport", value)} />
              </div>
            </>
          )}

          <div className="tw-col-span-2 tw-w-full tw-z-20">
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-1">Date of Transaction</label>
            <ReactDatePicker showMonthDropdown showYearDropdown dropdownMode="select" selected={date} onChange={(val) => setDate(val)} className="tw-w-full tw-border tw-rounded-lg tw-h-11 tw-px-3 tw-text-sm focus:tw-outline-none" dateFormat="dd MMM, yyyy" popperPlacement="bottom-start" minDate={new Date("2022-01-01")} maxDate={new Date()} />
          </div>

          <div className="tw-col-span-9 tw-w-full">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-1">
              <label className="tw-block tw-text-sm tw-font-medium">HS Code</label>
              <span onClick={handleClassify} className="tw-cursor-pointer tw-text-primary-purple-500 tw-font-medium tw-text-sm tw-mt-1 tw-inline-block hover:tw-text-primary-purple-600">
                Not sure about the HS code? Get HS classification for your product
              </span>
            </div>
            <HsCodeAutosuggest isDisabled={!autosuggestCounty} country={autosuggestCounty} selectedItem={selectedHSCode} tradeDirection={tradeDirection} handleChange={(selectedvalue) => setSelectedHSCode(selectedvalue)} module={"compare_tariff"} />
          </div>

          <div className="tw-col-span-3 tw-w-full tw-flex tw-items-end">
            <button data-tip={IS_SOURCE_ATLAS ? "" : "Viewing duties costs 2 value add credits for each country"} data-for="compare-search" onClick={getduties} disabled={isSearchDisabled} className="tw-bg-primary-purple-500 disabled:tw-opacity-60 tw-text-white tw-font-medium tw-rounded-lg tw-px-6 tw-py-2 tw-w-full tw-text-sm tw-h-11">
              View Tariffs and Duties
            </button>

            <TooltipWrapper>
              <ReactTooltip className="trademo-tooltip !tw-rounded-full !tw-bg-[#fff6ed] !tw-border !tw-border-[#dc6803] !tw-text-[#dc6803] !tw-py-1 !tw-px-4 !tw-max-w-80" place="bottom" id="compare-search" effect="solid" />
            </TooltipWrapper>
          </div>
        </div>

        {hsCodeInfoData && (
          <div>
            {formattedResult.length > 1 && (
              <div className="tw-flex tw-items-center tw-justify-between tw-py-4 tw-sticky tw-top-[48px] tw-bg-white tw-z-10">
                <div className="tw-flex tw-items-center tw-gap-3">
                  {formattedResult.map(([countryCode], index) => (
                    <div key={countryCode} onClick={() => countryJumper(countryCode, index)} className="tw-cursor-pointer tw-flex tw-font-medium tw-items-center tw-gap-2 tw-px-2 tw-py-1 tw-text-md tw-border tw-border-gray-[#e7e7e7] tw-rounded-lg tw-w-fit tw-bg-white tw-shadow-autoSuggest">
                      <ReactCountryFlag svg countryCode={countryCode} />
                      {countryMapping[countryCode]}
                    </div>
                  ))}
                </div>

                <div className={`tw-bg-[#ffffff] !tw-border !tw-border-[#e7e7e7] tw-rounded-lg tw-px-4 tw-py-2 tw-flex tw-items-center tw-gap-2 ${downloading ? "tw-opacity-50 tw-cursor-not-allowed" : "tw-cursor-pointer"}`} onClick={!downloading ? downloadDutyReport : undefined} data-for="justification-report" data-tip={IS_SOURCE_ATLAS ? "" : "Downloading justification costs 3 value add credits"}>
                  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#1c1b1f">
                    <path d="M480-336 288-528l51-51 105 105v-342h72v342l105-105 51 51-192 192ZM263.72-192Q234-192 213-213.15T192-264v-72h72v72h432v-72h72v72q0 29.7-21.16 50.85Q725.68-192 695.96-192H263.72Z" />
                  </svg>
                  <span className="tw-text-gray-900 tw-font-medium tw-text-sm">{downloading ? "Downloading..." : "Download"}</span>
                </div>
              </div>
            )}

            <div className="tw-space-y-3">
              {formattedResult.map(([countryCode, data], index) => (
                <div key={countryCode} id={`${countryCode}-jumper`} className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                  {formattedResult.length === 1 ? (
                    <>
                      <div className="tw-w-full tw-py-2.5 tw-text-left tw-flex tw-justify-between tw-items-center tw-bg-gray-50 tw-p-4 tw-font-medium">
                        <span className="tw-font-medium tw-text-gray-900 tw-text-md tw-flex tw-gap-2 tw-items-center">
                          <ReactCountryFlag svg countryCode={countryCode} />
                          {countryMapping[countryCode]}
                        </span>

                        <div className="tw-flex-shrink-0 tw-ml-auto">
                          <div className={`tw-bg-[#ffffff] !tw-border !tw-border-[#e7e7e7] tw-rounded-lg tw-px-4 tw-py-2 tw-flex tw-items-center tw-gap-2 ${downloading ? "tw-opacity-50 tw-cursor-not-allowed" : "tw-cursor-pointer"}`} onClick={!downloading ? downloadDutyReport : undefined} data-for="justification-report-single" data-tip={IS_SOURCE_ATLAS ? "" : "Downloading justification costs 3 value add credits"}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#1c1b1f">
                              <path d="M480-336 288-528l51-51 105 105v-342h72v342l105-105 51 51-192 192ZM263.72-192Q234-192 213-213.15T192-264v-72h72v72h432v-72h72v72q0 29.7-21.16 50.85Q725.68-192 695.96-192H263.72Z" />
                            </svg>
                            <span className="tw-text-gray-900 tw-font-medium tw-text-sm">{downloading ? "Downloading..." : "Download"}</span>
                          </div>
                        </div>
                      </div>

                      <div className="tw-px-4 tw-bg-white">
                        <TariffsData tradeDirection={tradeDirection} countryOfImport={selectedCountry.countryOfImport} data={data} />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="tw-w-full tw-py-2.5 tw-text-left tw-flex tw-justify-between tw-items-center tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500 tw-focus:ring-inset tw-bg-gray-50 tw-p-4 tw-font-medium tw-cursor-pointer" onClick={() => toggleAccordion(index)} type="button">
                        <span className="tw-font-medium tw-text-gray-900 tw-text-md tw-flex tw-gap-2 tw-items-center">
                          <ReactCountryFlag svg countryCode={countryCode} />
                          {countryMapping[countryCode]}
                        </span>
                        {openAccordions[index] ? <ChevronUp className="tw-h-5 tw-w-5 tw-text-gray-500 tw-transition-transform tw-duration-200" /> : <ChevronDown className="tw-h-5 tw-w-5 tw-text-gray-500 tw-transition-transform tw-duration-200" />}
                      </div>

                      {openAccordions[index] && (
                        <div className="tw-px-4 tw-bg-white">
                          <TariffsData tradeDirection={tradeDirection} countryOfImport={selectedCountry.countryOfImport} data={data} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TariffAnalyser;
